import type { Directive, DirectiveBinding } from 'vue'
import { useUserStore } from '../stores/user'

/**
 * 权限检查指令 v-hasPermi
 * 用法:
 * <button v-hasPermi="'users:create'">新增用户</button>
 * <button v-hasPermi="['users:edit', 'users:admin']">编辑</button>
 */

function checkPermission(value: string | string[]): boolean {
  const userStore = useUserStore()
  const permissions = userStore.permissions
  
  if (!value || !permissions || permissions.length === 0) {
    return false
  }
  
  // 超级管理员拥有所有权限
  if (permissions.includes('*:*:*')) {
    return true
  }
  
  // 处理数组权限（满足其中一个即可）
  if (Array.isArray(value)) {
    return value.some(perm => hasSinglePermission(permissions, perm))
  }
  
  // 处理单个权限
  return hasSinglePermission(permissions, value)
}

function hasSinglePermission(permissions: string[], permission: string): boolean {
  // 直接匹配
  if (permissions.includes(permission)) {
    return true
  }
  
  // 通配符匹配 (如 system:user:* 匹配 system:user:create)
  const parts = permission.split(':')
  for (let i = parts.length - 1; i > 0; i--) {
    const wildcard = [...parts.slice(0, i), '*'].join(':')
    if (permissions.includes(wildcard)) {
      return true
    }
  }
  
  return false
}

const hasPermi: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const { value } = binding
    
    if (!value) {
      console.warn('v-hasPermi 指令需要一个权限值')
      return
    }
    
    const hasAuth = checkPermission(value)
    
    if (!hasAuth) {
      // 无权限时移除元素
      el.parentNode?.removeChild(el)
    }
  }
}

/**
 * 权限检查指令 v-hasAllPermi（需要满足所有权限）
 * 用法:
 * <button v-hasAllPermi="['users:edit', 'users:delete']">编辑并删除</button>
 */
const hasAllPermi: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const { value } = binding
    
    if (!value || !Array.isArray(value)) {
      console.warn('v-hasAllPermi 指令需要一个权限数组')
      return
    }
    
    const userStore = useUserStore()
    const permissions = userStore.permissions
    
    // 超级管理员拥有所有权限
    if (permissions.includes('*:*:*')) {
      return
    }
    
    // 需要满足所有权限
    const hasAllAuth = value.every(perm => hasSinglePermission(permissions, perm))
    
    if (!hasAllAuth) {
      el.parentNode?.removeChild(el)
    }
  }
}

/**
 * 角色检查指令 v-hasRole
 * 用法:
 * <button v-hasRole="'admin'">管理员操作</button>
 * <button v-hasRole="['admin', 'superAdmin']">高级操作</button>
 */
const hasRole: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    const { value } = binding
    
    if (!value) {
      console.warn('v-hasRole 指令需要一个角色值')
      return
    }
    
    const userStore = useUserStore()
    const roles = userStore.userInfo?.roles || []
    
    // 超级管理员角色
    if (roles.includes('admin') || roles.includes('superAdmin')) {
      return
    }
    
    const requiredRoles = Array.isArray(value) ? value : [value]
    const hasRoleAuth = requiredRoles.some(role => roles.includes(role))
    
    if (!hasRoleAuth) {
      el.parentNode?.removeChild(el)
    }
  }
}

export { hasPermi, hasAllPermi, hasRole }

// 默认导出 v-hasPermi
export default hasPermi
